import React, {useEffect, useState} from 'react';
import Button from '../../../components/Button';
import {useData} from '../../../hooks/useData';
import {doFetch} from '../../../util/fetch';
import {useServerId} from "../useServerId";
import {LucideTriangleAlert} from "lucide-react";
import {useOverlay} from "../overlay/OverlayContext";

interface ServerSettings {
    name: string;
    rconIp: string;
    rconPort: string;
    rconPassword: string;
    rconSecure: boolean;
    isDisabled: boolean;
    mode: string;
    hasPluginRconToken: boolean;
    deleteScheduledAt: number | null;
}

interface SettingsFormErrors {
    name?: string;
    ip?: string;
    port?: string;
    password?: string;
    post?: string;
}

export default function Settings() {

    const serverId = useServerId()
    const overlay = useOverlay()

    const [serverForm, setServerForm] = useState({ name: "" });
    const [rconForm, setRconForm] = useState({
        ip: "",
        port: "",
        password: "",
        https: false,
    });

    const [serverErrors, setServerErrors] = useState<SettingsFormErrors>({});
    const [rconErrors, setRconErrors] = useState<SettingsFormErrors>({});

    const [serverLoading, setServerLoading] = useState(false);
    const [rconLoading, setRconLoading] = useState(false);
    const [cancelDeleteLoading, setCancelDeleteLoading] = useState(false);

    // Connection mode + plugin RCON token
    const [pluginRcon, setPluginRcon] = useState(false);
    const [pluginToken, setPluginToken] = useState<string | null>(null);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [tokenError, setTokenError] = useState<string>();
    const [copied, setCopied] = useState(false);

    const { data: settings, isLoading, error } = useData<ServerSettings>(`/api/client/servers/settings?serverId=${serverId}`);

    useEffect(() => {
        if (settings) {
            setServerForm({ name: settings.name });
            setRconForm({
                ip: settings.rconIp,
                port: settings.rconPort,
                password: settings.rconPassword,
                https: settings.rconSecure,
            });
            setPluginRcon(settings.mode === "PluginRcon");
        }
    }, [settings]);

    const handleServerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setServerForm({ ...serverForm, [name]: value });
    };

    const handleRconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setRconForm({ ...rconForm, [name]: type === "checkbox" ? checked : value });
    };

    const validateServer = () => {
        const newErrors: SettingsFormErrors = {};
        if (!serverForm.name) newErrors.name = "Name is required.";
        setServerErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateRcon = () => {
        const newErrors: SettingsFormErrors = {};
        if (!rconForm.ip) newErrors.ip = "IP address is required.";
        else if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(rconForm.ip)) newErrors.ip = "Invalid IP address.";
        if (!rconForm.port) newErrors.port = "Port is required.";
        else if (!/^\d{1,5}$/.test(rconForm.port)) newErrors.port = "Invalid port number.";
        if (!rconForm.password) newErrors.password = "Password is required.";
        setRconErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleServerSubmit = async () => {
        if (!validateServer()) return;

        setServerLoading(true);
        const response = await doFetch(`/api/client/servers/settings?serverId=${serverId}`, {
            body: JSON.stringify({ name: serverForm.name }),
            headers: { "Content-Type": "application/json" },
            method: "POST"
        });
        setServerLoading(false);

        if (response.ok) {
            setServerErrors({});
        } else {
            const error = response.errorString ?? "Unknown error";
            setServerErrors({ post: error });
        }
    };

    const handleRconSubmit = async () => {
        // Vanilla mode needs valid credentials; plugin mode just flips the mode.
        if (!pluginRcon && !validateRcon()) return;

        setRconLoading(true);
        const response = await doFetch<{success: boolean, error?: string}>(`/api/client/servers/settings/rcon?serverId=${serverId}`, {
            body: JSON.stringify(pluginRcon
                ? { pluginRcon: true }
                : {
                    pluginRcon: false,
                    ip: rconForm.ip,
                    port: rconForm.port,
                    password: rconForm.password,
                    secure: rconForm.https
                }),
            headers: { "Content-Type": "application/json" },
            method: "POST"
        });
        setRconLoading(false);

        if (response.ok && response.data?.success === true) {
            setRconErrors({});
            if (!pluginRcon) {
                // Clear password after successful submission for security
                setRconForm({ ...rconForm, password: "" });
            }
        } else {
            const error = response.data?.error ?? response.errorString ?? "Unknown error";
            setRconErrors({ post: error });
        }
    };

    const generateToken = async (): Promise<string | undefined> => {
        setTokenLoading(true);
        setTokenError(undefined);
        const response = await doFetch<{success: boolean, token?: string}>(`/api/client/servers/settings/pluginrcon/token?serverId=${serverId}`, {
            body: JSON.stringify({}),
            headers: { "Content-Type": "application/json" },
            method: "POST"
        });
        setTokenLoading(false);

        if (response.ok && response.data?.success === true && response.data.token) {
            setPluginToken(response.data.token);
            setCopied(false);
            return undefined;
        }

        const err = response.errorString ?? "Failed to generate token";
        setTokenError(err);
        return err;
    };

    const handleGenerateToken = () => {
        const hasToken = pluginToken !== null || settings?.hasPluginRconToken === true;
        if (hasToken) {
            overlay.showConfirmation("Generate a new token?", generateToken,
                "This invalidates the previous token. Any plugin still using the old token will disconnect until you update its config.");
        } else {
            void generateToken();
        }
    };

    const handleCopyToken = async () => {
        if (!pluginToken) return;
        try {
            await navigator.clipboard.writeText(pluginToken);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard may be unavailable; the field is selectable as a fallback
        }
    };

    const handleDisable = async (disable: boolean) => {

        const doDisable = async () => {
            const response = await doFetch(`/api/client/servers/state?serverId=${serverId}`, {
                body: JSON.stringify({ disable: disable }),
                headers: { "Content-Type": "application/json" },
                method: "POST"
            });
            if (response.ok) {
                window.location.reload();
            } else {
                return response.errorString ?? "Unknown error";
            }
        }

        if (disable){
            overlay.showConfirmation(`Disable '${settings?.name}'?`, doDisable,
                "Are you sure you want to disable this server? RustServerTool will no longer connect to it, but you can still access all data.")
        }
        else{
            overlay.showConfirmation(`Enable '${settings?.name}'?`, doDisable,
                "Are you sure you want to enable this server? RustServerTool will now connect to it again.")
        }
    }

    const handleDelete = () => {
        const doDelete = async () => {
            const response = await doFetch(`/api/client/servers/delete?serverId=${serverId}`, {
                body: JSON.stringify({ cancel: false }),
                headers: { "Content-Type": "application/json" },
                method: "POST"
            });
            if (response.ok) {
                window.location.reload();
            } else {
                return response.errorString ?? "Unknown error";
            }
        }

        overlay.showConfirmation(`Delete '${settings?.name}'?`, doDelete,
            "This permanently deletes the server and ALL associated data (players, stats, bans, console history, …). " +
            "The server is disabled immediately and will be deleted in 24 hours. You can undo this any time before then.")
    }

    const handleCancelDelete = async () => {
        setCancelDeleteLoading(true);
        const response = await doFetch(`/api/client/servers/delete?serverId=${serverId}`, {
            body: JSON.stringify({ cancel: true }),
            headers: { "Content-Type": "application/json" },
            method: "POST"
        });
        if (response.ok) {
            window.location.reload();
        } else {
            setCancelDeleteLoading(false);
        }
    }

    if (isLoading) {
        return (
            <div className="size-full flex flex-col p-4 relative">
                <div className="flex justify-center items-center h-full">
                    <div className="text-white">Loading settings...</div>
                </div>
            </div>
        );
    }

    if (error || !settings) {
        return (
            <div className="size-full flex flex-col p-4 relative">
                <div className="flex justify-center items-center h-full">
                    <div className="text-red-400">Error loading settings: {error}</div>
                </div>
            </div>
        );
    }


    return (
        <div className="size-full flex flex-col p-4 relative overflow-y-auto">
            <h1 className="text-2xl font-bold text-white mb-4">Server Settings</h1>

            <div className="space-y-6">
                {/* Section 1: Server Settings */}
                <div className="rounded-md p-4 border border-neutral-700">
                    <h2 className="text-xl font-semibold mb-4">General</h2>

                    <div className="space-y-4">
                        <div>
                            <label className="block font-medium">Server Name</label>
                            <input
                                name="name"
                                value={serverForm.name}
                                onChange={handleServerChange}
                                className="mt-1 w-full rst-input"
                            />
                            {serverErrors.name && <p className="text-red-400 text-sm mt-1">{serverErrors.name}</p>}
                        </div>

                        <div className="flex justify-end">
                            <Button
                                loading={serverLoading}
                                text="Save"
                                onClick={handleServerSubmit}
                                className="bg-primary hover:bg-primary-hover"
                            />
                        </div>

                        {serverErrors.post && <p className="text-red-400 text-sm mt-1">Error: {serverErrors.post}</p>}
                    </div>
                </div>

                {/* Section 2: RCON */}
                <div className="rounded-md p-4 border border-neutral-700">
                    <h2 className="text-xl font-semibold mb-4">RCON</h2>

                    <div className="space-y-6">
                        {/* Connection Mode */}
                        <div className="space-y-4">
                            <div>
                                <label className="block font-medium">Connection Mode</label>
                                <select
                                    value={pluginRcon ? "PluginRcon" : "Rcon"}
                                    onChange={(e) => setPluginRcon(e.target.value === "PluginRcon")}
                                    className="mt-1 w-full rst-input"
                                >
                                    <option value="PluginRcon">Plugin RCON (Recommended)</option>
                                    <option value="Rcon">Vanilla RCON</option>
                                </select>
                                {pluginRcon ? <p className="text-sm text-neutral-400 mt-2">
                                    Plugin RCON is a more secure alternative to Rust's vanilla RCON and requires the <a
                                    className="text-primary hover:underline" rel="noreferrer"
                                    href="https://codefling.com/plugins/rust-server-tool" target="_blank">RustServerTool
                                    plugin</a> on
                                    your server. This increases security because there is no need to expose rcon ports
                                    to the public.
                                </p> :
                                <p className="text-sm text-neutral-400 mt-2">
                                Vanilla RCON is a direct connection using your server's RCON credentials.
                                </p>}
                            </div>
                        </div>

                        <div className="border-t border-neutral-700"></div>

                        {pluginRcon ? (
                            /* Plugin RCON */
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Plugin RCON</h3>

                                <p className="text-sm text-neutral-300">
                                    Paste the generated token into the config file <code className="text-neutral-200">RustServerTool.json</code> under{" "}
                                    <code className="text-neutral-200">"Plugin RCON token"</code> and reload the plugin.
                                </p>

                                {pluginToken ? (
                                    <div className="space-y-2">
                                        <label className="block font-medium">Your Plugin RCON Token</label>
                                        <div className="flex gap-2">
                                            <input
                                                readOnly
                                                value={pluginToken}
                                                onFocus={(e) => e.target.select()}
                                                className="flex-1 rst-input font-mono text-sm"
                                            />
                                            <Button
                                                text={copied ? "Copied" : "Copy"}
                                                onClick={handleCopyToken}
                                                className="bg-neutral-700 hover:bg-neutral-600"
                                            />
                                        </div>
                                        <p className="text-xs text-amber-400">
                                            Copy it now — it won't be shown again. Keep it secret.
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-sm text-neutral-400">
                                        {settings.hasPluginRconToken
                                            ? "A token has already been generated. Generate a new one if you've lost it (this invalidates the old token)."
                                            : "No token generated yet. Generate one to connect your plugin."}
                                    </p>
                                )}

                                <div className="flex justify-end">
                                    <Button
                                        loading={tokenLoading}
                                        text={pluginToken || settings.hasPluginRconToken ? "Regenerate Token" : "Generate Token"}
                                        onClick={handleGenerateToken}
                                        className="bg-primary hover:bg-primary-hover"
                                    />
                                </div>

                                {tokenError && <p className="text-red-400 text-sm">Error: {tokenError}</p>}
                            </div>
                        ) : (
                            /* Vanilla RCON */
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Vanilla RCON</h3>

                                <div className="flex space-x-2">
                                    <div className="flex-1">
                                        <label className="block font-medium">Server IP</label>
                                        <input
                                            name="ip"
                                            value={rconForm.ip}
                                            onChange={handleRconChange}
                                            className="mt-1 block w-full rst-input"
                                        />
                                        {rconErrors.ip && <p className="text-red-400 text-sm mt-1">{rconErrors.ip}</p>}
                                    </div>
                                    <div className="w-1/3">
                                        <label className="block font-medium">RCON Port</label>
                                        <input
                                            name="port"
                                            value={rconForm.port}
                                            onChange={handleRconChange}
                                            type="number"
                                            className="mt-1 w-full rst-input"
                                        />
                                        {rconErrors.port && <p className="text-red-400 text-sm mt-1">{rconErrors.port}</p>}
                                    </div>
                                </div>

                                <div>
                                    <label className="block font-medium">RCON Password</label>
                                    <input
                                        name="password"
                                        type="password"
                                        value={rconForm.password}
                                        onChange={handleRconChange}
                                        placeholder="Enter new password"
                                        className="mt-1 block w-full rst-input"
                                    />
                                    {rconErrors.password && <p className="text-red-400 text-sm mt-1">{rconErrors.password}</p>}
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        id="rcon-https"
                                        name="https"
                                        type="checkbox"
                                        checked={rconForm.https}
                                        onChange={handleRconChange}
                                        className="h-5 w-5 text-blue-500 focus:ring-blue-500 border-neutral-700 bg-neutral-800"
                                    />
                                    <label htmlFor="rcon-https" className="font-medium select-none">
                                        RCON over HTTPS
                                    </label>
                                </div>

                            </div>
                        )}

                        <div className="flex justify-end">
                            <Button
                                loading={rconLoading}
                                text="Save"
                                onClick={handleRconSubmit}
                                className="bg-primary hover:bg-primary-hover"
                            />
                        </div>

                        {rconErrors.post && <p className="text-red-400 text-sm">Error: {rconErrors.post}</p>}
                    </div>
                </div>

                {/* Section 4: Danger Zone */}
                <div className="rounded-md p-4 border border-neutral-700">
                    <h2 className="text-xl font-semibold mb-6 text-red-500 flex gap-2 items-center"><LucideTriangleAlert size={20} /> Danger Zone</h2>

                    <div className="space-y-6">
                        {settings.deleteScheduledAt ? (
                            <div className="space-y-2">
                                <p className="text-red-400">This server is scheduled for permanent deletion
                                    on <b>{new Date(settings.deleteScheduledAt * 1000).toLocaleString()}</b>. The server and all
                                    associated data will be deleted then. Undo below to keep your server.</p>
                                <Button
                                    loading={cancelDeleteLoading}
                                    text="Undo Deletion"
                                    onClick={handleCancelDelete}
                                    className="border border-neutral-500 hover:bg-neutral-700"
                                />
                            </div>
                        ) : (
                            <>
                                {!settings.isDisabled ? <div className="space-y-2">
                                    <p>When the server is disabled, RustServerTool will not connect to your server, but you can
                                        still access all data. You can re-enable the server at any time.</p>
                                    <button onClick={() => handleDisable(true)}
                                        className="px-4 py-2 rounded-lg border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white">Disable
                                        Server
                                    </button>
                                </div> : <div className="space-y-2">
                                    <p>Your server is currently disabled. RustServerTool will not connect to it.</p>
                                    <button onClick={() => handleDisable(false)}
                                        className="px-4 py-2 rounded-lg hover:bg-neutral-700 border border-neutral-500 hover:text-white">Enable
                                        Server
                                    </button>
                                </div>}
                                <div className="space-y-2">
                                    <p>When the server is deleted, all data associated with this server will be deleted as
                                        well. You have 24 hours to undo this before it becomes permanent.</p>
                                    <button onClick={handleDelete}
                                        className="px-4 py-2 rounded-lg border border-red-500 text-red-500 hover:bg-red-500 hover:text-white">Delete
                                        Server
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
